import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Alert } from "@mui/material";

import { useMetricsData } from "./hooks/useMetricsData";
import { useBusinessDay } from "../../hooks/useBusinessDay";
import { formatPeriodShort, getPeriodRange, isDayString } from "../../utils/periods";
import PeriodSelector from '../../components/PeriodSelector';
import FilterMenu from "./components/FilterMenu";
import MetricsGrid from "./components/MetricsGrid";
import GroupSelector from "./components/GroupSelector";
import FiltersTopRow from "../../components/FiltersTopRow";
import { useMemo } from "react";
import StickyFiltersBar from "../../components/StickyFiltersBar";
import NoReportAccess from "../../components/NoReportAccess";

/**
 * Главная страница метрик продаж.
 * Содержит выбор периода, фильтры и визуализацию данных + тенденции.
 */
const MetricsPage = ({ selectedGroup: propSelectedGroup, setSelectedGroup: propSetSelectedGroup, allGroups: propAllGroups, activeServerName: propActiveServerName }) => {
  const [period, setPeriod] = useState(localStorage.getItem("selectedPeriod") || "today");
  const [selectedPayType, setSelectedPayType] = useState("Все");
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  
  // Используем переданные значения или локальные (для обратной совместимости)
  const selectedGroup = propSelectedGroup || "Все";
  const setSelectedGroup = propSetSelectedGroup || (() => {});


  // 🔹 Период — от учётного дня ресторана; пока он грузится, данные не запрашиваем (null)
  const businessDay = useBusinessDay();
  // ?day=yyyy-MM-dd — разовый день из сводки после закрытия смены; в localStorage не сохраняется
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedDay = isDayString(searchParams.get("day")) ? searchParams.get("day") : null;
  const dateRange = useMemo(() => {
    if (linkedDay) return { from: linkedDay, to: linkedDay };
    return businessDay ? getPeriodRange(period, businessDay) : null;
  }, [linkedDay, period, businessDay]);
  const token = localStorage.getItem("token");

  // Используем переданное значение или загружаем из localStorage
  const activeServerName = propActiveServerName || (() => {
    try {
      const cachedServer = localStorage.getItem("activeServer");
      if (cachedServer) {
        const server = JSON.parse(cachedServer);
        return server?.server_name || server?.name || null;
      }
    } catch (e) {
      console.error("Ошибка чтения активного сервера:", e);
    }
    return null;
  })();

  // 🔹 Загружаем данные и тренды через хук
  const {
    filteredData,
    trends, // 🔹 тенденции (дельты в %)
    allPayTypes,
    allGroups: hookAllGroups,
    loading,
    error,
    accessDenied,
  } = useMetricsData(token, dateRange, selectedPayType, selectedGroup, linkedDay ? "day" : period);
  
  // Используем переданные группы или из хука
  const allGroups = propAllGroups || hookAllGroups;

  // Обработчик смены периода
  // Обработчик смены периода; выбор периода убирает разовый день из ссылки
  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (linkedDay) {
      const next = new URLSearchParams(searchParams);
      next.delete("day");
      setSearchParams(next, { replace: true });
    }
  };


  // Обработчики меню фильтров
  const handleOpenFilters = (e) => setFilterMenuAnchor(e.currentTarget);
  const handleCloseFilters = () => setFilterMenuAnchor(null);

  return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "#fff",
          width: "100%",
        }}
      >
        {/* Ошибка */}
        {error && (
          <Alert
            severity="error"
            onClose={() => {}}
            sx={{
              mb: 2,
              width: "100%",
              backgroundColor: "rgba(255, 50, 50, 0.1)",
              border: "1px solid rgba(255, 100, 100, 0.3)",
              color: "#fff",
              whiteSpace: "pre-line", // текст iiko бывает многострочным
            }}
          >
            {error}
          </Alert>
        )}

        <StickyFiltersBar>
          <FiltersTopRow>
            <GroupSelector
              groups={allGroups}
              selectedGroup={selectedGroup}
              onSelectGroup={setSelectedGroup}
              activeServerName={activeServerName}
              sx={{ mb: 0 }}
            />
          </FiltersTopRow>

          <PeriodSelector
            period={linkedDay ? "day" : period}
            dayItem={linkedDay ? { key: "day", label: formatPeriodShort(dateRange) } : null}
            rangeLabel={linkedDay ? "" : formatPeriodShort(dateRange)}
            onChange={handlePeriodChange}
            onOpenFilters={handleOpenFilters}
            showFilters={false}
          />
        </StickyFiltersBar>

        {/* Сетка карточек метрик + тренды; нет прав на отчёты iiko — мягкая карточка вместо них */}
        {accessDenied ? (
          <NoReportAccess />
        ) : (
          <MetricsGrid
            filteredData={filteredData}
            trends={trends} // 🔹 передаём дельты
            loading={loading || !businessDay}
            error={error}
            token={token}
          />
        )}

        {/* Меню фильтров */}
        <FilterMenu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={handleCloseFilters}
          payTypes={allPayTypes}
          groups={allGroups}
          selectedPayType={selectedPayType}
          selectedGroup={selectedGroup}
          onSelectPayType={(val) => {
            setSelectedPayType(val);
            handleCloseFilters();
          }}
          onSelectGroup={(val) => {
            setSelectedGroup(val);
            handleCloseFilters();
          }}
        />
      </Box>
  );
};

export default MetricsPage;
